import {
  type LifecycleEventRepository,
  type LifecycleEvent,
} from "../interfaces";
import { supabase } from "@/integrations/supabase/supabaseClient";

export class SupabaseLifecycleEventRepository
  implements LifecycleEventRepository
{
  private async getCurrentUserId(): Promise<string> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated");
    return user.id;
  }

  async getAll(): Promise<LifecycleEvent[]> {
    await this.getCurrentUserId(); // Ensure user is authenticated

    const { data, error } = await supabase
      .from("lifecycle_events")
      .select(
        `
        *,
        subscriptions!inner(user_id)
      `,
      )
      .order("event_date", { ascending: false });

    if (error) throw error;
    return data.map((item) => ({
      id: item.id,
      subscription_id: item.subscription_id,
      event: item.event as
        | "activated"
        | "cancelled"
        | "reactivated"
        | "trial_started"
        | "trial_ended"
        | "paused"
        | "resumed",
      event_date: item.event_date,
      reason: item.reason,
      notes: item.notes,
      created_at: item.created_at,
    }));
  }

  async getBySubscriptionId(subscriptionId: string): Promise<LifecycleEvent[]> {
    await this.getCurrentUserId(); // Ensure user is authenticated

    const { data, error } = await supabase
      .from("lifecycle_events")
      .select("*")
      .eq("subscription_id", subscriptionId)
      .order("event_date", { ascending: false });

    if (error) throw error;
    return data.map((item) => ({
      ...item,
      event: item.event as
        | "activated"
        | "cancelled"
        | "reactivated"
        | "trial_started"
        | "trial_ended"
        | "paused"
        | "resumed",
    }));
  }

  async create(
    event: Omit<LifecycleEvent, "id" | "created_at">,
  ): Promise<LifecycleEvent> {
    await this.getCurrentUserId(); // Ensure user is authenticated

    const { data, error } = await supabase
      .from("lifecycle_events")
      .insert(event)
      .select()
      .single();

    if (error) throw error;
    return {
      ...data,
      event: data.event as
        | "activated"
        | "cancelled"
        | "reactivated"
        | "trial_started"
        | "trial_ended"
        | "paused"
        | "resumed",
    };
  }

  async update(
    id: string,
    updates: Partial<LifecycleEvent>,
  ): Promise<LifecycleEvent> {
    await this.getCurrentUserId(); // Ensure user is authenticated

    const { data, error } = await supabase
      .from("lifecycle_events")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return {
      ...data,
      event: data.event as
        | "activated"
        | "cancelled"
        | "reactivated"
        | "trial_started"
        | "trial_ended"
        | "paused"
        | "resumed",
    };
  }

  async delete(id: string): Promise<void> {
    await this.getCurrentUserId(); // Ensure user is authenticated

    const { error } = await supabase
      .from("lifecycle_events")
      .delete()
      .eq("id", id);

    if (error) throw error;
  }

  async bulkCreate(
    events: Omit<LifecycleEvent, "id" | "created_at">[],
  ): Promise<LifecycleEvent[]> {
    await this.getCurrentUserId(); // Ensure user is authenticated

    const { data, error } = await supabase
      .from("lifecycle_events")
      .insert(events)
      .select();

    if (error) throw error;
    return data.map((item) => ({
      ...item,
      event: item.event as
        | "activated"
        | "cancelled"
        | "reactivated"
        | "trial_started"
        | "trial_ended"
        | "paused"
        | "resumed",
    }));
  }

  async deleteAll(): Promise<void> {
    const userId = await this.getCurrentUserId();

    const { error } = await supabase
      .from("lifecycle_events")
      .delete()
      .in(
        "subscription_id",
        (
          await supabase
            .from("subscriptions")
            .select("id")
            .eq("user_id", userId)
        ).data?.map((s) => s.id) || [],
      );

    if (error) throw error;
  }
}
